// The worklist a counter opens: the counts that are OPEN FOR COUNTING.
//
// §4.5's worksheet works on ONE count, so it needs an entry point, and the
// entry point is deliberately narrow: `status=in_progress`. A `draft` count has
// no snapshot yet (§3.4 step 2 — starting it is the storekeeper's office
// action), and a count in `review`/`pending_approval`/`posted` is not a
// counter's screen at all. Offering them here would send a floor worker to a
// worksheet that answers 409 `COUNT_STATE_INVALID` on the first line.
//
// Offline this list is a CACHED snapshot, and it says so: a count that was
// submitted while the device was in a dead zone is still listed, and the
// worksheet behind it is the surface that discovers the state change. The
// alternative — hiding the list offline — would strand a counter who walked
// into the aisle with the app already open.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { floorRequestId } from "../lib/floor-errors";
import { loadRead, readCacheKey } from "../lib/floor-read-cache";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import { formatBusinessDate } from "../../../lib/datetime";
import { facilityTodayIso } from "../../../lib/business-dates";
import { useTenantTimezone } from "../../../lib/role-codes";
import type { components } from "@assessly/api-client";

type CycleCountList = components["schemas"]["CycleCountListResponse"];

/** Scope names are read in one batch of at most this many ids (§5 row 1's
 * `id=`), the batch the worksheet reads its bins in. */
const SCOPE_BATCH = 50;

/** The scope paths live under ONE snapshot key, merged across reads, so a new
 * count on the list does not leave every other card without its location in
 * a dead zone (a key built from the ids changed with every new count). */
const SCOPES_CACHE_KEY = readCacheKey("count-scopes", "paths");

/** Past this many remembered paths the snapshot keeps only the list's own. */
const SCOPES_CACHE_CAP = 4 * SCOPE_BATCH;

/** Pure: the paths to remember after a read, the fresh answer over what the
 * device already held, trimmed to the list's own ids once it grows past the
 * cap (unit-tested). */
export function mergeScopePaths(
  held: Readonly<Record<string, string>> | undefined,
  fresh: Readonly<Record<string, string>>,
  listIds: readonly string[],
): Record<string, string> {
  const merged: Record<string, string> = { ...held, ...fresh };
  if (Object.keys(merged).length <= SCOPES_CACHE_CAP) return merged;
  const keep = new Set([...listIds, ...Object.keys(fresh)]);
  return Object.fromEntries(Object.entries(merged).filter(([id]) => keep.has(id)));
}

/** Pure: the business date a count started on, in the tenant's calendar. A
 * count started at 02:00 in Almaty is that day's, not the UTC day before
 * (unit-tested). */
export function countStartedDate(startedAt: string, timezone: string | undefined): string {
  const at = Date.parse(startedAt);
  return Number.isNaN(at) ? startedAt : facilityTodayIso(at, timezone);
}

export function FloorCountsScreen() {
  const { t, i18n } = useTranslation();
  const timezone = useTenantTimezone();
  const read = useCachedRead<CycleCountList>({
    queryKey: ["floor", "counts", "in_progress"],
    cacheKey: readCacheKey("counts", "in_progress"),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/cycle-counts", {
          signal,
          params: { query: { status: "in_progress", limit: 50 } },
        }),
        "cycle-counts",
      ),
  });

  const counts = read.data?.items ?? [];

  // WHERE each count is (D-444 ruling 6): its scope location's path, read for
  // exactly the scopes on the list and cached like the list, so a counter in a
  // dead aisle still sees "Main / Aisle 3" rather than a bare code.
  const scopeIds = [...new Set(counts.map((c) => c.scope_location_id))]
    .sort()
    .slice(0, SCOPE_BATCH);
  const scopes = useCachedRead<Record<string, string>>({
    queryKey: ["floor", "counts", "scopes", scopeIds.join(",")],
    cacheKey: SCOPES_CACHE_KEY,
    enabled: scopeIds.length > 0,
    queryFn: async ({ signal }) => {
      const page = unwrap(
        await client.GET("/v1/inventory/locations", {
          params: { query: { id: scopeIds, limit: SCOPE_BATCH } },
          signal: floorReadSignal(signal),
        }),
        "locations",
      );
      const fresh = Object.fromEntries(page.items.map((row) => [row.id, row.path]));
      return mergeScopePaths(
        loadRead<Record<string, string>>(SCOPES_CACHE_KEY)?.value,
        fresh,
        scopeIds,
      );
    },
  });

  return (
    <FloorScreen
      wide
      title={t("inventory.floor.counts.title")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      <SnapshotNote source={read.source} cachedAt={read.cachedAt} />
      {read.isLoading ? <FloorSkeleton rows={3} /> : null}

      {/* A read that did not come back is NOT an empty worklist. `source === "none"`
          is "no live answer and nothing cached": online that means the request
          failed (error + retry + support code), offline it means this device has
          never loaded the list. Falling through to FloorEmpty here would tell a
          counter "no count has been started for you yet" off a 500 and send them
          away from real work — the same class of lie §4.7 refuses for
          "0 customers affected". Mirrors the worksheet half (floor-count.tsx). */}
      {read.source === "none" && !read.isLoading ? (
        read.online ? (
          <FloorError
            title={t("inventory.floor.counts.loadFailedTitle")}
            detail={t("inventory.floor.counts.loadFailedBody")}
            requestId={floorRequestId(read.error)}
            onRetry={read.refetch}
          />
        ) : (
          <FloorNeedsConnection what={t("inventory.floor.counts.thisList")} />
        )
      ) : null}

      {!read.isLoading && read.source !== "none" && counts.length === 0 ? (
        <FloorEmpty
          title={t("inventory.floor.counts.emptyTitle")}
          body={
            read.online
              ? t("inventory.floor.counts.emptyBody")
              : t("inventory.floor.counts.emptyOfflineBody")
          }
        />
      ) : null}

      <ul className="floor-cards" data-testid="floor-counts-list">
        {counts.map((count) => {
          const where = scopes.data?.[count.scope_location_id];
          return (
            <li key={count.id}>
              {/* The whole card opens the worksheet (D-444 ruling 6): which
                  count, where it is, and since when. */}
              <Link
                to="/floor/counts/$id"
                params={{ id: count.id }}
                className="floor-card floor-card-link"
                data-testid="floor-count-open"
              >
                <span className="floor-card-title">{count.count_code}</span>
                {where !== undefined ? <span className="floor-card-sub">{where}</span> : null}
                {count.started_at !== null ? (
                  <span className="floor-card-meta">
                    {t("home.floor.started", {
                      date: formatBusinessDate(
                        countStartedDate(count.started_at, timezone),
                        i18n.language,
                      ),
                    })}
                  </span>
                ) : null}
                {count.blind ? (
                  <span className="floor-badge floor-badge-warn" data-testid="floor-count-blind">
                    {t("inventory.floor.counts.blind")}
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </FloorScreen>
  );
}
